import { useEffect } from 'react'
import { withDependencies } from '@wix/thunderbolt-ioc'
import {
	IRenderDoneObserver,
	ILogger,
	LOADING_PHASES,
	LoggerSymbol,
	RenderDoneCallback,
	LayoutHook,
	BrowserWindowSymbol,
} from '@wix/thunderbolt-symbols'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import { initLayoutNotifier } from './LayoutNotifier'

export const RenderDoneObserver = withDependencies(
	[BrowserWindowSymbol, NavigationManagerSymbol, LoggerSymbol],
	(window: Window, navigationManager: INavigationManager, logger: ILogger): IRenderDoneObserver & LayoutHook => {
		let renderPending: boolean = false
		let renderPendingComps: Array<string> = []
		let dispatchBeforePaint: boolean = false
		let isWaitingForDispatch: boolean = false

		const layoutNotifier = initLayoutNotifier<RenderDoneCallback>()

		const setRenderPending: IRenderDoneObserver['setRenderPending'] = (isPending, pendingComps = []) => {
			renderPendingComps = pendingComps
			renderPending = isPending
		}

		const raf = (task: () => void) => {
			window.requestAnimationFrame(task)
		}

		const notifyObservers = () => {
			const dispatch = () => {
				logger.phaseStarted(LOADING_PHASES.DS_INVOKE_RENDER_DONE_HANDLERS)
				layoutNotifier.notify()
				logger.phaseEnded(LOADING_PHASES.DS_INVOKE_RENDER_DONE_HANDLERS)

				setRenderPending(false)
			}

			// check if this condition can be removed since it will be always true - waiting for navigation
			// https://jira.wixpress.com/browse/TB-6166
			if (dispatchBeforePaint) {
				// measure comps before paint (before changes are applied to the DOM)
				// needed for scenarios like drag/resize and push where we need to measure pushed components before dragged component is updated with new mesh calc
				dispatch()
			} else {
				if (!isWaitingForDispatch) {
					isWaitingForDispatch = true
					raf(() => {
						dispatch()
						isWaitingForDispatch = false
					})
				}
			}
			dispatchBeforePaint = false
		}

		const notifyIfNeeded = () => {
			renderPendingComps = []
			// check if relevant
			setRenderPending(true, renderPendingComps)
			notifyObservers()
		}

		// check if can remove - https://jira.wixpress.com/browse/TB-6166
		const onFirstNavigation = async () => {
			logger.phaseStarted(LOADING_PHASES.DS_WAIT_FOR_DID_MOUNT)
			if (navigationManager.isDuringNavigation()) {
				await navigationManager.waitForNavigationEnd()
			}
			dispatchBeforePaint = true
			logger.phaseEnded(LOADING_PHASES.DS_WAIT_FOR_DID_MOUNT)
			unregisterToNavigation()
		}

		const unregisterToNavigation = navigationManager.registerToNavigationStart(onFirstNavigation)

		return {
			subscribe: layoutNotifier.subscribe,
			unsubscribe: layoutNotifier.unsubscribe,
			getDiagnosticState: () => ({
				RD_isRenderPending: renderPending,
				RD_renderCausingComps: renderPendingComps,
			}),
			useObserver: () => {
				useEffect(() => {
					notifyIfNeeded()
				})
			},
			isRenderPending: () => renderPending,
			setRenderPending,
		}
	}
)
